<script>
  import { useLocation } from "svelte-navigator";
  import { useUrlParams } from "@stores/urlStore.js";
  import { activitiesStoreProject } from "@stores/projectStores.js";
  import { writable, get } from "svelte/store";
  import { tick } from "svelte";
  import {
    SvelteFlow,
    MarkerType,
    MiniMap,
    Background,
    ConnectionMode,
    useSvelteFlow,
  } from "@xyflow/svelte";

  import "@xyflow/svelte/dist/style.css";
  import { dagre } from "dagre-d3";
  import * as d3 from "d3";
  import * as dagreD3 from "dagre-d3";
  import GraphGrouper from "lib/components/GraphGrouperTasks.svelte";
  import CustomNode from "./CustomNodeTask.svelte";
  import CustomEdge from "./PercentageEdge.svelte";
  import CustomGroupNode from "./CustomGroupNode.svelte";
  import WorkflowExplorerDropdownMenu from "./workflowExplorerDropdownMenuTasks.svelte";
  import { onMount } from "svelte";
  import { formatMinSec } from "@utils/formatfunctions.js";

  export let currentActivityId = "";
  export let nodesData = [];
  export let edgesData = [];
  export let subgraphs = [];
  export let kpis = {};
  export let width = "500px";
  export let height = "500px";
  export let selectedNode = "";
  export let selectedCluster = "";
  export let fullNodeInfo = false;
  export let updateZoomLevel;
  export let selectedNodes = [];
  export let expandedNodes = [];
  export let triggerUpdate;
  export let executionFrequency = 1;
  export let minFrequency = 0;
  export let nnodes = 0;
  export let nedges = 0;
  export let seeMinimap = false;
  export let activitiesToShow = [];
  export let edgesToShow = [];
  export let graphActivities = [];
  export let colorMetric = "none";
  export let appTypesByName = {};
  export let windowSequences = {};
  export let opportunitySteps = new Set();
  export let opportunityCards = [];
  export let clearOnEmpty = false;
  export let simulationPath = [];
  export let simulationStep = 0;
  export let previewMode = false;
  export let editActivity = () => {};
  export let explainActivity = null;

  const { getZoom, viewport } = useSvelteFlow();
  const nodes = writable([]);
  const edges = writable([]);

  let zoomLevel = writable((getZoom() * 100).toFixed(0));
  let currentParentNode;
  let isActivityNode = false;
  let contextMenuOpen = false;
  let menuPosition = { x: 0, y: 0 };
  let subprocessMenuOpen = false;
  let isLoadingGraph = false;
  let originalEdgePoints = {};
  let originalNodePositions = {};
  let originalNodeStyles = {};
  let expandedNodeDeltas = {};
  let metricsExpandedNodes = new Set();
  const ACTIVITY_HEADER_HEIGHT = 59;
  const ACTIVITY_GROUP_WIDTH_REDUCTION = 90;
  const ACTIVITY_GROUP_HEIGHT_REDUCTION = 18;
  const ACTIVITY_CHILD_LAYOUT_WIDTH_FACTOR = 0.76;
  // Keep enough room below expanded nodes for the edge label and arrowhead.
  // A smaller gap lets the expanded content consume the complete edge corridor,
  // which makes labels and arrows appear attached to the node border.
  const EXPANDED_NODE_GAP = 96;

  const nodeTypes = {
    custom: CustomNode,
    group: CustomGroupNode,
  };

  const edgeTypes = {
    customEdg: CustomEdge,
  };

  const {
    //getActivityByNode,
    removeRecordsFromActivity,
  } = activitiesStoreProject;

  $: ({ allActivities } = $activitiesStoreProject);

  $: activitiesToShow = $nodes;
  let filteredEdges = [];
  $: edgesToShow = filteredEdges;

  $: {
    if ($viewport.zoom != zoomLevel) {
      updateZoomLevel();
      closeContextMenu();
    }
  }

  $: {
    {
      nodesData, edgesData, fullNodeInfo, minFrequency, nnodes, nedges, colorMetric, appTypesByName, opportunityCards;
    }
    if ((nodesData.length && edgesData.length) || clearOnEmpty) {
      resetData().then(() => setData());
    }
  }

  $: {
    simulationPath, simulationStep;
    applySimulationStep();
  }

  const svgNS = "http://www.w3.org/2000/svg";
  let svg;
  let flowContainerElement;

  export async function downloadPNG(selectedProcesses) {
    const flowContainer = flowContainerElement?.querySelector(".svelte-flow");
    if (!flowContainer) return;
    await ensureAllImagesBase64(flowContainer);
    await generateSVG(selectedProcesses, flowContainer);

    const serializer = new XMLSerializer();
    const svgString = serializer.serializeToString(svg);

    const svgUrl = URL.createObjectURL(
      new Blob([svgString], { type: "image/svg+xml;charset=utf-8" }),
    );
    try {
      const image = new Image();
      image.src = svgUrl;
      await image.decode();

      const canvas = document.createElement("canvas");
      canvas.width = Number(svg.getAttribute("width"));
      canvas.height = Number(svg.getAttribute("height"));
      const context = canvas.getContext("2d");
      if (!context || !canvas.width || !canvas.height) {
        throw new Error("Could not create a canvas for the graph image");
      }
      context.fillStyle = "white";
      context.fillRect(0, 0, canvas.width, canvas.height);
      context.drawImage(image, 0, 0);

      const blob = await new Promise((resolve, reject) => {
        canvas.toBlob((result) => {
          if (result) resolve(result);
          else reject(new Error("Could not create PNG image"));
        }, "image/png");
      });
      const pngUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      const processPrefix = (selectedProcesses || [])
        .map((process) => process.name)
        .filter(Boolean)
        .join("-");
      link.href = pngUrl;
      link.download = `${processPrefix ? `${processPrefix}-` : ""}task_explorer.png`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(pngUrl), 1000);
    } finally {
      URL.revokeObjectURL(svgUrl);
    }
  }

  export function updateWindowSequences(windowSequences = {}) {
    nodes.update((currentNodes) =>
      currentNodes.map((node) => {
        const windowSequence = windowSequences[node.id] || [];
        return {
          ...node,
          data: {
            ...node.data,
            windowSequence,
            hasWindowSequence: Boolean(windowSequence.length),
          },
        };
      }),
    );
  }

  async function ensureAllImagesBase64(flowContainer) {
    if (!flowContainer) return;
    const allImages = flowContainer.querySelectorAll("img");
    const imagePromises = [];
    allImages.forEach((img) => {
      if (img.src && !img.src.startsWith("data:")) {
        const promise = fetch(img.src)
          .then((response) => response?.ok ? response.blob() : null)
          .then(
            (blob) =>
              blob && new Promise((resolve) => {
                const reader = new FileReader();
                reader.onload = () => {
                  img.src = reader.result;
                  resolve();
                };
                reader.readAsDataURL(blob);
              }),
          )
          .catch(() => console.warn("Could not convert image:", img.src));
        imagePromises.push(promise);
      }
    });
    await Promise.all(imagePromises);
  }
  export async function downloadSVG(selectedProcesses) {
    const flowContainer = flowContainerElement?.querySelector(".svelte-flow");
    if (!flowContainer) return;
    await ensureAllImagesBase64(flowContainer);

    const rect = flowContainer.getBoundingClientRect();
    const graphElements = flowContainer.querySelectorAll(
      ".svelte-flow__node, .svelte-flow__edge, .edge-label",
    );
    const bounds = Array.from(graphElements).reduce(
      (result, element) => {
        const elementRect = element.getBoundingClientRect();
        if (!elementRect.width && !elementRect.height) return result;
        return {
          minX: Math.min(result.minX, elementRect.left - rect.left),
          minY: Math.min(result.minY, elementRect.top - rect.top),
          maxX: Math.max(result.maxX, elementRect.right - rect.left),
          maxY: Math.max(result.maxY, elementRect.bottom - rect.top),
        };
      },
      { minX: Infinity, minY: Infinity, maxX: -Infinity, maxY: -Infinity },
    );
    if (!Number.isFinite(bounds.minX)) return;

    const padding = 32;
    const width = Math.ceil(bounds.maxX - bounds.minX + padding * 2);
    const height = Math.ceil(bounds.maxY - bounds.minY + padding * 2);
    const exportSvg = document.createElementNS(svgNS, "svg");
    exportSvg.setAttribute("xmlns", svgNS);
    exportSvg.setAttribute("xmlns:xlink", "http://www.w3.org/1999/xlink");
    exportSvg.setAttribute("width", width);
    exportSvg.setAttribute("height", height);
    exportSvg.setAttribute("viewBox", `0 0 ${width} ${height}`);

    const background = document.createElementNS(svgNS, "rect");
    background.setAttribute("width", "100%");
    background.setAttribute("height", "100%");
    background.setAttribute("fill", "white");
    exportSvg.appendChild(background);

    const foreignObject = document.createElementNS(svgNS, "foreignObject");
    foreignObject.setAttribute("width", width);
    foreignObject.setAttribute("height", height);
    const clone = flowContainer.cloneNode(true);
    clone.setAttribute("xmlns", "http://www.w3.org/1999/xhtml");
    inlineStyles(flowContainer, clone);
    clone.style.width = `${width}px`;
    clone.style.height = `${height}px`;
    clone.style.background = "white";
    clone.querySelectorAll(
      ".svelte-flow__background, .svelte-flow__controls, .svelte-flow__minimap, .svelte-flow__attribution",
    ).forEach((node) => node.remove());
    const viewport = flowContainer.querySelector(".svelte-flow__viewport");
    const viewportClone = clone.querySelector(".svelte-flow__viewport");
    if (viewport && viewportClone) {
      const transform = getComputedStyle(viewport).transform;
      const match = transform.match(
        /^matrix\(([^,]+),\s*([^,]+),\s*([^,]+),\s*([^,]+),\s*([^,]+),\s*([^,]+)\)$/,
      );
      if (match) {
        viewportClone.style.transform = `matrix(${match[1]}, ${match[2]}, ${match[3]}, ${match[4]}, ${Number(match[5]) - bounds.minX + padding}, ${Number(match[6]) - bounds.minY + padding})`;
      }
    }
    foreignObject.appendChild(clone);
    exportSvg.appendChild(foreignObject);

    const blob = new Blob([new XMLSerializer().serializeToString(exportSvg)], {
      type: "image/svg+xml;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);

    let fileName = "";
    (selectedProcesses || []).forEach((process) => {
      fileName += process.name + "-";
    });
    fileName += "task_explorer.svg";
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }

  function inlineStyles(source, target) {
    const computed = getComputedStyle(source);
    Array.from(computed).forEach((property) => {
      target.style.setProperty(
        property,
        computed.getPropertyValue(property),
        computed.getPropertyPriority(property),
      );
    });
    Array.from(source.children).forEach((child, index) => {
      const targetChild = target.children[index];
      if (targetChild) inlineStyles(child, targetChild);
    });
  }

  export async function generateSVG(
    selectedProcesses,
    flowContainer = flowContainerElement?.querySelector(".svelte-flow"),
  ) {
    svg = document.createElementNS(svgNS, "svg");
    if (!flowContainer) return;

    // Get viewport transform
    const viewport = flowContainer.querySelector(".svelte-flow__viewport");
    const transform = viewport ? viewport.style.transform : "";

    const translateMatch = transform.match(/translate\(([^)]+)\)/);
    const scaleMatch = transform.match(/scale\(([^)]+)\)/);

    const [translateX = 0, translateY = 0] = translateMatch
      ? translateMatch[1].split(",").map((v) => parseFloat(v))
      : [0, 0];
    const scale = scaleMatch ? parseFloat(scaleMatch[1]) : 1;

    // Calculate SVG dimensions
    const nodes = flowContainer.querySelectorAll(".svelte-flow__node");
    const allowedGroupStyles = [
      "width",
      "height",
      "border-radius",
      "border",
      "background-color",
      "padding",
      "z-index",
      "text-align",
    ];
    const edgeLabels = flowContainer.querySelectorAll(".edge-label");
    let minX = Infinity,
      minY = Infinity,
      maxX = -Infinity,
      maxY = -Infinity;

    // Include calculation-centric nodes
    nodes.forEach((node) => {
      const transform = node.style.transform;
      const match = transform.match(/translate\(([^)]+)\)/);
      if (match) {
        const [x, y] = match[1].split(",").map((v) => parseFloat(v));

        const actualHeight = Math.max(node.offsetHeight, node.scrollHeight) + 4;

        minX = Math.min(minX, x);
        minY = Math.min(minY, y);
        maxX = Math.max(maxX, x + Math.max(node.offsetWidth, node.scrollWidth));
        maxY = Math.max(maxY, y + actualHeight);
      }
    });

    // Include edge labels in the calculation
    edgeLabels.forEach((label) => {
      const transform = label.style.transform;
      const position = getLastTranslate(transform);
      if (position) {
        const [x, y] = position;
        const labelRect = label.getBoundingClientRect();
        const labelWidth = labelRect.width;
        const labelHeight = labelRect.height;

        minX = Math.min(minX, x - labelWidth / 2);
        minY = Math.min(minY, y - labelHeight / 2);
        maxX = Math.max(maxX, x + labelWidth / 2);
        maxY = Math.max(maxY, y + labelHeight / 2);
      }
    });

    const padding = 100;
    if (!Number.isFinite(minX) || !Number.isFinite(minY)) return;
    const svgWidth = maxX - minX + padding * 2;
    const svgHeight = maxY - minY + padding * 2;

    svg.setAttribute("width", svgWidth);
    svg.setAttribute("height", svgHeight);
    svg.setAttribute("viewBox", `0 0 ${svgWidth} ${svgHeight}`);

    // white background
    const bg = document.createElementNS(svgNS, "rect");
    bg.setAttribute("width", "100%");
    bg.setAttribute("height", "100%");
    bg.setAttribute("fill", "white");
    svg.appendChild(bg);

    // Get styles
    const targetClasses = [
      ".svelte-flow",
      ".custom-node",
      ".container",
      ".node-header",
      ".custom-node:not(.start-end-node) .node-header",
      ".label-node",
      ".label-meta",
      ".custom-node:not(.start-end-node) .label-meta",
      ".node-app-icon",
      ".node-app-icon img",
      ".missing-app-icon",
      ".meta-row",
      ".meta-row span",
      ".edge-label",
      ".edge-label-icon",
      ".edge-label-text",
      ".edge-label-main",
      ".edge-label-sub",
      ".start-end-node",
      ".more_filled_icon",
      ".icon-btn",
      ".custom-node:not(.start-end-node) .icon-btn",
      ".custom-node:not(.start-end-node) .icon-btn img",
      ".activity-expand-btn",
      ".sequence-toggle-symbol",
      ".sequence-toggle-symbol path",
      ".window-sequence-panel",
      ".window-sequence-row",
      ".window-occurrence-count",
      ".window-sequence-row span",
      ".window-sequence-row strong",
      ".expand-btn",
      ".metrics-panel",
      ".metrics-info",
      ".metrics-settings",
      ".metrics-settings svg",
      ".metrics-tooltip",
      ".metrics-grid",
      ".metric-item",
      ".metric-label",
      ".metric-value",
      ".action-list",
      ".action-row",
      ".action-row span",
      ".action-row p",
      ".action-pagination",
      ".action-pagination button",
      "button",
      ".svelte-flow__edges",
      ".svelte-flow__edge-path",
      ".svelte-flow__node",
      ".svelte-flow__node-group",
      ".connectable",
      ".selectable",
      ".parent",
      ".group-node",
      ".activity-header",
      ".activity-title",
      ".activity-separator",
      ".button-collapse",
    ];
    const extractedStyles = extractClassStyles(targetClasses);

    // Add additional styles
    const style = document.createElementNS(svgNS, "style");
    style.textContent = `
      ${extractedStyles}
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
    * {
      font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
      letter-spacing: 0.16px;
      box-sizing: border-box !important;
    }
      foreignObject {
        overflow: visible;
      }
      .button-collapse button{
       padding: 0;
    }
      .custom-node:not(.start-end-node) .node-header {
        display: grid;
        grid-template-columns: 44px minmax(0, 1fr) 32px;
        grid-auto-rows: min-content;
        column-gap: 6px;
        row-gap: 2px;
        align-items: center;
        padding: 6px 0;
        background: linear-gradient(
          90deg,
          transparent 0 calc(100% - 33px),
          rgba(0, 0, 0, 0.1) calc(100% - 33px) calc(100% - 32px),
          rgba(255, 255, 255, 0.35) calc(100% - 32px) 100%
        );
      }
      .custom-node:not(.start-end-node) .label-meta {
        grid-column: 2;
        grid-row: 1 / span 2;
        min-width: 0;
      }
      .window-sequence-panel {
        position: relative;
        z-index: 2;
        width: 100%;
        max-width: 100%;
        box-sizing: border-box;
        padding: 7px 10px 8px 12px;
        background: #fff;
        border-top: 1px solid #000;
        border-radius: 0 0 6px 6px;
        overflow: hidden;
      }
      .window-sequence-row {
        display: grid;
        grid-template-columns: 2.3rem minmax(0, 1fr) auto;
        gap: 0.5rem;
        align-items: center;
        width: 100%;
        max-width: 100%;
        min-height: 24px;
        border-bottom: 1px solid #dedede;
        color: #111827;
        font-size: 11px;
        font-weight: 600;
        line-height: 1.25;
        overflow: hidden;
      }
      .window-sequence-row:last-child {
        border-bottom: none;
      }
      .window-sequence-row span {
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
      .window-count {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        min-width: 1.35rem;
        box-sizing: border-box;
        border: 1px solid #d1d5db;
        border-radius: 5px;
        padding: 1px 5px;
        background: #fff;
        font-size: 10px;
        font-weight: 700;
      }
      .window-occurrence-count {
        justify-self: start;
        min-width: 2rem;
        color: #111827;
      }
      .window-count-tooltip {
        display: none !important;
      }
      .node-app-icon {
        grid-column: 1;
        grid-row: 1 / span 2;
        align-self: stretch;
        display: flex;
        align-items: center;
        justify-content: center;
        border-right: 1px solid rgba(0, 0, 0, 0.1);
      }
      .node-app-icon img,
      .missing-app-icon {
        width: 28px;
        height: 28px;
        border-radius: 6px;
      }
      .missing-app-icon {
        background: rgba(255, 255, 255, 0.28);
        display: flex;
        align-items: center;
        justify-content: center;
        color: rgba(0, 0, 0, 0.55);
        font-size: 13px;
        font-weight: 700;
      }
      .metrics-panel {
        position: relative;
        z-index: 5;
        width: 100%;
        box-sizing: border-box;
        margin: 0;
        padding: 10px 12px 12px 46px;
        background: #fff;
        border-top: 1px solid #000;
        border-radius: 0 0 6px 6px;
      }
      .metrics-info {
        position: absolute;
        left: 12px;
        top: 8px;
        width: 14px;
        height: 14px;
        padding: 0;
        border: 1px solid #1c1c20;
        border-radius: 50%;
        background: #fff;
        color: #1c1c20;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 9.5px;
        font-weight: 700;
        line-height: 1;
      }
      .metrics-tooltip,
      .metrics-settings {
        display: none !important;
      }
      .metrics-grid {
        display: grid;
        grid-template-columns: 1fr 1fr;
        row-gap: 8px;
        align-items: center;
        position: relative;
      }
      .metrics-grid::after {
        content: "";
        position: absolute;
        left: 50%;
        top: -4px;
        bottom: -4px;
        width: 1px;
        background: rgba(0, 0, 0, 0.15);
        transform: translateX(-50%);
      }
      .metric-item {
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 6px;
        font-size: 10px;
        font-weight: 600;
      }
      .metric-item:nth-child(odd) {
        padding-right: 12px;
      }
      .metric-item:nth-child(even) {
        padding-left: 12px;
      }
      .metric-label {
        color: #000;
      }
      .metric-value {
        font-weight: 600;
      }
      .metrics-panel.with-actions {
        padding: 8px 10px;
      }
      .metrics-panel.with-actions .metrics-grid,
      .metrics-panel.with-actions .metrics-info {
        display: none;
      }
      .action-list {
        margin: 0;
        font-size: 11px;
        font-weight: 600;
        line-height: 1.35;
        text-align: left;
      }
      .action-row {
        display: grid;
        grid-template-columns: 1.5rem minmax(0, 1fr);
        gap: 0.35rem;
        padding: 0.35rem 0;
        border-bottom: 1px solid #d9d9d9;
      }
      .action-row p {
        margin: 0;
        white-space: normal;
        overflow-wrap: anywhere;
      }
      .action-pagination {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 0.5rem;
        margin-top: 0.5rem;
        font-size: 11px;
        font-weight: 700;
      }
    `;
    svg.appendChild(style);

    // Convert images to base64
    const imagePromises = [];
    nodes.forEach((node) => {
      const images = node.querySelectorAll("img");
      images.forEach((img) => {
        if (img.src && !img.src.startsWith("data:")) {
          const promise = fetch(img.src)
            .then((response) => response?.ok ? response.blob() : null)
            .then(
              (blob) =>
                blob && new Promise((resolve) => {
                  const reader = new FileReader();
                  reader.onload = () => {
                    img.src = reader.result;
                    resolve();
                  };
                  reader.readAsDataURL(blob);
                }),
            )
            .catch(() => console.warn("Could not convert image:", img.src));

          imagePromises.push(promise);
        }
      });
    });

    await Promise.all(imagePromises);

    // print main group with offset
    const mainGroup = document.createElementNS(svgNS, "g");
    mainGroup.setAttribute(
      "transform",
      `translate(${padding - minX}, ${padding - minY})`,
    );

    // copy markers
    const defs = document.createElementNS(svgNS, "defs");
    const markers = flowContainer.querySelectorAll(
      ".svelte-flow__marker marker",
    );
    markers.forEach((marker) => {
      defs.appendChild(marker.cloneNode(true));
    });
    svg.appendChild(defs);

    // Create SVG group for edges below nodes
    const edgesGroup = document.createElementNS(svgNS, "g");
    edgesGroup.setAttribute("class", "svelte-flow__edges");

    // Copy edges paths
    const edges = flowContainer.querySelectorAll(
      ".svelte-flow__edge path.svelte-flow__edge-path",
    );
    edges.forEach((edge) => {
      const pathClone = edge.cloneNode(true);
      edgesGroup.appendChild(pathClone);
    });

    mainGroup.appendChild(edgesGroup);

    // copy nodes
    nodes.forEach((node) => {
      const transform = node.style.transform;
      const match = transform.match(/translate\(([^)]+)\)/);
      if (!match) return;

      const [x, y] = match[1].split(",").map((v) => parseFloat(v));

      const nodeRect = node.getBoundingClientRect();
      const actualWidth = nodeRect.width;
      const actualHeight = nodeRect.height;

      const foreignObject = document.createElementNS(svgNS, "foreignObject");
      foreignObject.setAttribute("x", x);
      foreignObject.setAttribute("y", y);
      foreignObject.setAttribute("width", actualWidth);
      foreignObject.setAttribute("height", actualHeight);
      if (node.classList.contains("parent")) {
        let specificStyles = "";
        allowedGroupStyles.forEach((prop) => {
          const value = node.style.getPropertyValue(prop);
          if (value) {
            specificStyles += `${prop}: ${value}; `;
          }
        });
        foreignObject.setAttribute("style", specificStyles);
        foreignObject.setAttribute("class", node.classList.value);
      }

      const nodeContent = node.querySelector(".container");
      if (nodeContent) {
        const contentClone = nodeContent.cloneNode(true);
        contentClone
          .querySelectorAll(".window-count-tooltip")
          .forEach((tooltip) => tooltip.remove());
        contentClone.querySelectorAll(".window-sequence-panel").forEach((panel) => {
          panel.style.maxWidth = "100%";
          panel.style.overflow = "hidden";
        });
        contentClone.querySelectorAll(".window-sequence-row").forEach((row) => {
          row.style.width = "100%";
          row.style.maxWidth = "100%";
          row.style.overflow = "hidden";
        });
        contentClone.querySelectorAll(".window-sequence-row span").forEach((span) => {
          span.style.minWidth = "0";
          span.style.overflow = "hidden";
          span.style.textOverflow = "ellipsis";
          span.style.whiteSpace = "nowrap";
        });
        contentClone.querySelectorAll(".window-count").forEach((badge) => {
          badge.style.display = "inline-flex";
          badge.style.alignItems = "center";
          badge.style.justifyContent = "center";
          badge.style.minWidth = "1.35rem";
          badge.style.boxSizing = "border-box";
          badge.style.border = "1px solid #d1d5db";
          badge.style.borderRadius = "5px";
          badge.style.padding = "1px 5px";
          badge.style.background = "#fff";
          badge.style.fontSize = "10px";
          badge.style.fontWeight = "700";
        });
        foreignObject.appendChild(contentClone);
        mainGroup.appendChild(foreignObject);
      }
    });

    // Copy edge labels
    edgeLabels.forEach((label) => {
      const transform = label.style.transform;
      const position = getLastTranslate(transform);
      if (position) {
        const [x, y] = position;

        const labelRect = label.getBoundingClientRect();
        const labelWidth = labelRect.width;
        const labelHeight = labelRect.height;

        const foreignObject = document.createElementNS(svgNS, "foreignObject");
        foreignObject.setAttribute("x", x - labelWidth / 2);
        foreignObject.setAttribute("y", y - labelHeight / 2);
        foreignObject.setAttribute("width", labelWidth);
        foreignObject.setAttribute("height", labelHeight);

        const labelClone = label.cloneNode(true);
        labelClone.style.position = "static";
        labelClone.style.transform = "";

        foreignObject.appendChild(labelClone);
        mainGroup.appendChild(foreignObject);
      }
    });

    svg.appendChild(mainGroup);
  }

  function getLastTranslate(transform) {
    const matches = [...transform.matchAll(/translate\(([^)]+)\)/g)];
    const match = matches[matches.length - 1];
    return match
      ? match[1].split(",").map((v) => parseFloat(v.replace("px", "")))
      : null;
  }

  function openNodeMenu(evt, id) {
    evt.preventDefault();
    evt.stopPropagation();
    if (contextMenuOpen) return (contextMenuOpen = !contextMenuOpen);
    selectedNode = id;
    let currentnode = nodesData.filter((node) => node.id === id)[0];

    if (currentnode.parentNode) {
      const activityId = currentnode.parentNode.split("_")[0];
      currentActivityId = activityId || "";
    } else currentActivityId = "";
    if (!currentnode.isSubProcess) {
      currentParentNode = currentnode.parentNode !== "" ? true : false;
      isActivityNode = true;
      selectedNode = id;
    } else {
      currentParentNode = false;
      isActivityNode = false;
      selectedCluster = id;
    }
    const iconBoundingRect = evt.target.getBoundingClientRect();
    menuPosition = {
      x: iconBoundingRect.left + window.scrollX,
      y: iconBoundingRect.top + window.scrollY,
    };
    contextMenuOpen = true;
  }

  function calculateNodeWidth(label, hasButton = true) {
    const iconWidth = 35;
    const buttonWidth = hasButton ? 30 : 0;
    const padding = 32;
    const border = 4;
    const gap = 8;

    const maxTextWidth = 340;

    const segments = label.split("<br>");
    let maxWidth = 0;
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    ctx.font = "14px Arial";

    segments.forEach((segment) => {
      const rawTextWidth = ctx.measureText(segment).width;

      const textWidth = Math.min(rawTextWidth, maxTextWidth);
      if (textWidth > maxWidth) {
        maxWidth = textWidth;
      }
    });

    const totalWidth =
      iconWidth +
      maxWidth +
      buttonWidth +
      padding +
      border +
      gap * (hasButton ? 2 : 1);

    return totalWidth;
  }

  function calculateNodeHeight(label) {
    const baseHeight = 70;
    const lineHeight = 5;

    const lines = label.split("<br>").length;
    const totalHeight = baseHeight + (lines - 1) * lineHeight;
    return totalHeight;
  }

  function filterGraphByFrequency(nodesIn, edgesIn, nnodes, nedges) {
    const rawEdges = edgesIn.filter((e) => e.from !== e.to);
    const edgeFrequency = (edge) =>
      edge.metricValue ??
      ((edge.transitionRate ?? edge.value / executionFrequency) || 0) * 100;

    function keepCompletePaths(edgeList) {
      const forward = new Map();
      const backward = new Map();
      edgeList.forEach((edge) => {
        if (!forward.has(edge.from)) forward.set(edge.from, []);
        if (!backward.has(edge.to)) backward.set(edge.to, []);
        forward.get(edge.from).push(edge.to);
        backward.get(edge.to).push(edge.from);
      });

      const walk = (start, map) => {
        const seen = new Set([start]);
        const queue = [start];
        while (queue.length) {
          const current = queue.shift();
          (map.get(current) || []).forEach((next) => {
            if (!seen.has(next)) {
              seen.add(next);
              queue.push(next);
            }
          });
        }
        return seen;
      };

      const fromStart = walk("Start", forward);
      const toEnd = walk("End", backward);
      return edgeList.filter(
        (edge) => fromStart.has(edge.from) && toEnd.has(edge.to),
      );
    }

    const allEdges =
      minFrequency > 0
        ? keepCompletePaths(
            rawEdges.filter((edge) => edgeFrequency(edge) >= minFrequency),
          )
        : rawEdges;
    const filteredNodeIds = new Set();
    allEdges.forEach((edge) => {
      filteredNodeIds.add(edge.from);
      filteredNodeIds.add(edge.to);
    });

    if (nnodes >= nodesIn.length && nedges >= allEdges.length) {
      return {
        nodes:
          minFrequency > 0
            ? nodesIn.filter((node) => filteredNodeIds.has(node.id))
            : nodesIn,
        edges: allEdges,
      };
    }

    // Greedy walk: follow highest-weight unvisited edge until End, O(V)

    function greedyPath(from, to) {
      const visited = new Set([]);
      visited.add(from);
      const pathEdges = [];
      let current = from;
      let currentPath = [from];

      while (current != to) {
        let neighbors = allEdges
          .filter((e) => e.from === current)
          .sort((a, b) => b.value - a.value);
        if (!neighbors) return null;
        let moved = false;
        for (const edge of neighbors) {
          if (!visited.has(edge.to)) {
            visited.add(edge.to);
            pathEdges.push(edge);
            current = edge.to;
            moved = true;
            currentPath.push(edge.to);
            break;
          }
        }
        if (!moved) {
          currentPath.pop();
          pathEdges.pop();
          if (currentPath.length === 0) return null;
          current = currentPath[currentPath.length - 1];
        }
      }
      return { nodes: currentPath, edges: pathEdges };
    }

    // Pick the best greedy path across all starts

    const initialPath = greedyPath("Start", "End");

    // Initialize result sets
    const resultNodeIds = new Set(initialPath.nodes);
    const resultEdgeKeys = new Set();
    for (const e of initialPath.edges) {
      resultEdgeKeys.add(`${e.from}->${e.to}`);
    }

    // Helper: check if a node can reach any End node through result edges
    // Function to check connectivity using BFS (equivalent to local_node_connectivity)
    function localNodeConnectivity(fromId, toId, edgeList) {
      if (fromId === toId) return true;

      const visited = new Set();
      const queue = [fromId];
      const adjacency = new Map();

      // Build adjacency map
      const allNodeIds = new Set();
      edgeList.forEach((e) => {
        allNodeIds.add(e.from);
        allNodeIds.add(e.to);
      });

      allNodeIds.forEach((nodeId) => adjacency.set(nodeId, []));
      edgeList.forEach((e) => {
        adjacency.get(e.from).push(e.to);
      });

      while (queue.length > 0) {
        const current = queue.shift();
        if (current === toId) return true;
        if (visited.has(current)) continue;

        visited.add(current);
        const neighbors = adjacency.get(current) || [];
        neighbors.forEach((neighbor) => {
          if (!visited.has(neighbor)) {
            queue.push(neighbor);
          }
        });
      }
      return false;
    }

    // Sort all edges by value desc once
    const edgesByValueDesc = allEdges.slice().sort((a, b) => b.value - a.value);

    // Add nnodes additional nodes
    for (let i = 0; i < nnodes; i++) {
      let addedNodeId = null;

      for (const edge of edgesByValueDesc) {
        if (resultNodeIds.has(edge.from) && !resultNodeIds.has(edge.to)) {
          addedNodeId = edge.to;
          break;
        }
        if (resultNodeIds.has(edge.to) && !resultNodeIds.has(edge.from)) {
          addedNodeId = edge.from;
          break;
        }
      }

      if (!addedNodeId) break;
      resultNodeIds.add(addedNodeId);

      // Ensure this node can reach End — if not, trace a greedy path to End
      if (
        !localNodeConnectivity(
          addedNodeId,
          "End",
          allEdges.filter((e) => resultEdgeKeys.has(`${e.from}->${e.to}`)),
        )
      ) {
        const pathToEnd = greedyPath(addedNodeId, "End");
        if (pathToEnd) {
          for (const e of pathToEnd.edges) {
            resultNodeIds.add(e.from);
            resultNodeIds.add(e.to);
            resultEdgeKeys.add(`${e.from}->${e.to}`);
          }
        }
      }

      // Ensure Start can reach this node — if not, trace path from Start
      if (
        !localNodeConnectivity(
          "Start",
          addedNodeId,
          allEdges.filter((e) => resultEdgeKeys.has(`${e.from}->${e.to}`)),
        )
      ) {
        const pathFromStart = greedyPath("Start", addedNodeId);
        if (pathFromStart) {
          for (const e of pathFromStart.edges) {
            resultNodeIds.add(e.from);
            resultNodeIds.add(e.to);
            resultEdgeKeys.add(`${e.from}->${e.to}`);
          }
        }
      }
    }

    // Add nedges additional edges between existing nodes
    let added = 0;
    for (const edge of edgesByValueDesc) {
      if (added >= nedges) break;
      const key = `${edge.from}->${edge.to}`;
      if (
        !resultEdgeKeys.has(key) &&
        resultNodeIds.has(edge.from) &&
        resultNodeIds.has(edge.to)
      ) {
        resultEdgeKeys.add(key);
        added++;
      }
    }

    // Build final lists
    const finalEdges = allEdges.filter((e) =>
      resultEdgeKeys.has(`${e.from}->${e.to}`),
    );
    const filteredNodes = nodesIn.filter((n) => resultNodeIds.has(n.id));

    return { nodes: filteredNodes, edges: finalEdges };
  }

  function calculateNodePositions(nodes, edges, fullNodeInfo) {
    const g = new dagreD3.graphlib.Graph({ compound: true }).setGraph({
      rankdir: "TB",
      nodesep: 250,
      ranksep: 125,
      transitions: true,
    });
    g.setDefaultEdgeLabel(() => ({}));

    g.graph().transition = function transition(selection) {
      return selection.transition().duration(1000);
    };

    const relevantNodes = new Set();

    //  Add filter by frequency
    let { nodes: filteredNodes, edges: filteredEdgesTemp } =
      filterGraphByFrequency(nodes, edges, nnodes, nedges);
    filteredEdges = filteredEdgesTemp;
    nodes = filteredNodes;
    edges = filteredEdgesTemp;

    // Make sure Start/End nodes are included if G is not empty
    const startNode = nodes.find(
      (n) =>
        n.label === "Start" ||
        (n.label && n.label.startsWith("Start")) ||
        (n.id && n.id.includes("Start")),
    );
    const endNode = nodes.find(
      (n) =>
        n.label === "End" ||
        (n.label && n.label.startsWith("End")) ||
        (n.id && n.id.includes("End")),
    );

    if (startNode) relevantNodes.add(startNode.id);
    if (endNode) relevantNodes.add(endNode.id);

    g.setNode("mainprocess_cluster", {
      label: "",
      labelType: "html",
      style: "fill:none;",
    });

    if (subgraphs) {
      subgraphs.forEach((node) => {
        g.setNode(node.clusterid, {
          label: node.label,
          labelType: "html",
          clusterLabelPos: "top",
          style: `fill:${node.color};`,
          rx: 8,
          ry: 8,
        });
        g.setParent(node.clusterid, "mainprocess_cluster");
      });
    }

    // Add parents of relevant nodes
    nodes.forEach((node) => {
      if (node.parentNode) {
        relevantNodes.add(node.parentNode);
      }
    });

    // Add filtered nodes to dagre
    nodes.forEach((node) => {
      let labelContent = fullNodeInfo
        ? node.label
        : node.shortlabel || node.label;

      const width = calculateNodeWidth(labelContent, true);
      const height = calculateNodeHeight(labelContent);
      const layoutWidth = node.parentNode
        ? Math.max(width * ACTIVITY_CHILD_LAYOUT_WIDTH_FACTOR, 280)
        : width + 13;

      if (node.label === "Start" || node.label === "End") {
        g.setNode(node.id, {
          label: labelContent,
          width: calculateNodeWidth(labelContent, false),
          height: height,
          style: node.color ? `fill: ${node.color}` : "",
        });
      } else {
        g.setNode(node.id, {
          label: labelContent,
          width: layoutWidth,
          height: height,
          style: node.color ? `fill: ${node.color}` : "",
        });
        if (node.parentNode) {
          g.setParent(node.id, node.parentNode);
        } else {
          g.setParent(node.id, "mainprocess_cluster");
        }
      }
    });

    // Make sure orphan nodes are included
    relevantNodes.forEach((nodeId) => {
      if (!g.hasNode(nodeId)) {
        g.setNode(nodeId, { label: "" });
      }
    });

    // Add all filtered edges, WITHOUT additional filtering by frequency
    edges.forEach((edge) => {
      if (!g.edge(edge.from, edge.to)) {
        const metricValue =
          edge.metricValue ??
          ((edge.outgoingTransitionRate ??
            edge.transitionRate ??
            edge.value / executionFrequency) || 0) * 100;
        g.setEdge(edge.from, edge.to, {
          label: edge.dashed
            ? ""
            : edge.transitionMetric === "occurrences"
              ? `${Math.round(metricValue)}x`
              : `${Math.min(metricValue, 100).toFixed(0)}%`,
          curve: d3.curveMonotoneY,
          minlen: 1,
          weight: 200,
          dashed: edge.dashed,
        });
      }
    });

    dagre.layout(g);

    // Position nodes
    const positionedNodes = nodes.map((node) => {
      const dagreNode = g.node(node.id);

      return {
        ...node,
        position: {
          x: dagreNode.x - 200,
          y: dagreNode.y - dagreNode.height / 2,
        },
      };
    });

    // Group positions
    const parentNodePositions = [];
    g.nodes().forEach((nodeId) => {
      const node = g.node(nodeId);
      if (g.parent(nodeId) && g.parent(nodeId) !== "mainprocess_cluster") {
        const parentNodeId = g.parent(nodeId);
        const parentNode = g.node(parentNodeId);
        if (parentNode) {
          parentNodePositions[parentNodeId] = {
            x: parentNode.x,
            y: parentNode.y,
            width: parentNode.width,
            height: parentNode.height,
            label: parentNode.label,
          };
        }
      }
    });

    // Create group nodes
    const parentNodes = Array.from(relevantNodes)
      .filter((nodeId) => !nodes.find((node) => node.id === nodeId))
      .map((nodeId) => {
        const dagreNode = g.node(nodeId);
        const parentPos = parentNodePositions[nodeId];
        const subgraphInfo =
          subgraphs.find((s) => s.clusterid === nodeId) || {};
        if (parentPos) {
          const { width, height, x, y, label } = parentPos;
          const childNode = nodes.find((node) => node.parentNode === nodeId);
          const color = childNode ? childNode.color : "";
          const groupWidth = width - ACTIVITY_GROUP_WIDTH_REDUCTION;
          const groupHeight =
            height + ACTIVITY_HEADER_HEIGHT - ACTIVITY_GROUP_HEIGHT_REDUCTION;
          return {
            ...subgraphInfo,
            id: nodeId,
            label,
            position: {
              x: x - groupWidth / 2,
              y: y - height / 2 - ACTIVITY_HEADER_HEIGHT,
            },
            type: "group",
            width: groupWidth,
            height: groupHeight,
            color,
            data: { label },
          };
        } else if (dagreNode) {
          return {
            ...subgraphInfo,
            id: nodeId,
            label: "",
            position: { x: dagreNode.x - 125, y: dagreNode.y - 81 },
            type: "group",
          };
        }
        return null;
      })
      .filter((node) => node !== null);

    const allPositionedNodes = [...parentNodes, ...positionedNodes];

    // Position edges
    const positionedEdges = g.edges().map((e) => {
      const edge = g.edge(e);
      const srcNode = g.node(e.v);
      const tgtNode = g.node(e.w);
      let points = edge.points ? edge.points.map((p) => ({ ...p })) : [];

      if (points.length >= 2 && srcNode && tgtNode) {
        const sHalfH = (srcNode.height || 0) / 2;
        const tHalfH = (tgtNode.height || 0) / 2;
        const srcIsStartEnd = /^(Start|End)( \d+)?$/.test(e.v);
        const tgtIsStartEnd = /^(Start|End)( \d+)?$/.test(e.w);

        if (srcNode.y <= tgtNode.y) {
          // Forward edge (down): exit source bottom, enter target top
          if (!srcIsStartEnd)
            points[0] = { x: points[0].x, y: srcNode.y + sHalfH };
          if (!tgtIsStartEnd)
            points[points.length - 1] = {
              x: points[points.length - 1].x,
              y: tgtNode.y - tHalfH,
            };
        } else {
          // Backward edge (up): exit source top, enter target bottom
          if (!srcIsStartEnd)
            points[0] = { x: points[0].x, y: srcNode.y - sHalfH };
          if (!tgtIsStartEnd)
            points[points.length - 1] = {
              x: points[points.length - 1].x,
              y: tgtNode.y + tHalfH,
            };
        }
      }

      return {
        id: `${e.v}-${e.w}`,
        source: e.v,
        target: e.w,
        points,
        label: edge.label,
        dashed: edge.dashed,
      };
    });

    // Adjust child node positions relative to their parents
    const finalPositionedNodes = allPositionedNodes.map((node) => {
      if (node.type === "group") return node;
      const parentNode = parentNodes.find(
        (parent) => parent.id === node.parentNode,
      );
      if (parentNode) {
        return {
          ...node,
          position: {
            x: node.position.x - parentNode.position.x,
            y: node.position.y - parentNode.position.y,
          },
        };
      }
      return node;
    });

    return {
      positionedNodes: finalPositionedNodes,
      edges: positionedEdges,
    };
  }

  function getActivityByNode(node) {
    if (!node) return null;
    const [nodeid, step_number] = node.split("_");
    for (const activity of graphActivities) {
      const recording = activity.activity_mapping_step_recordings.find(
        (reg) =>
          reg.recording_id === nodeid &&
          reg.step_number === parseInt(step_number),
      );
      if (recording) {
        return { ...recording, activity_id: activity.id };
      }
    }
    return null;
  }

  async function removeNodeFromActivity() {
    if (!selectedNodes.includes(selectedNode)) {
      selectedNodes = [...selectedNodes, selectedNode];
    }
    const recordingsToRemoveByActivity = {};
    await Promise.all(
      selectedNodes.map(async (node) => {
        const activity = await getActivityByNode(node);
        if (!activity) return;
        const activityId = activity.activity_id;
        const [nodeid, step_number] = node.split("_");
        if (!step_number) return;
        if (!recordingsToRemoveByActivity[activityId]) {
          recordingsToRemoveByActivity[activityId] = [];
        }
        recordingsToRemoveByActivity[activityId].push({
          recording_id: nodeid,
          step_number: parseInt(step_number),
        });
      }),
    );

    const promises = [];
    for (const activityId in recordingsToRemoveByActivity) {
      const listOfRecordingsToRemove = recordingsToRemoveByActivity[activityId];
      promises.push(
        removeRecordsFromActivity(
          listOfRecordingsToRemove,
          activityId,
          graphActivities,
        ),
      );
    }

    await Promise.all(promises);
    selectedNodes = [];
  }

  function addNodesToActivity() {
    subprocessMenuOpen = true;
  }

  async function expandActivity(evt, nodeId = selectedCluster) {
    evt?.preventDefault();
    evt?.stopPropagation();
    if (!nodeId) return;

    if (!expandedNodes.includes(nodeId)) {
      expandedNodes = [...expandedNodes, nodeId];
    }
    contextMenuOpen = false;
    await tick();
    triggerUpdate = true;
  }

  function applyExpansionDeltas() {
    // Sort expanded nodes top-to-bottom so deltas accumulate correctly
    const expandedEntries = Object.entries(expandedNodeDeltas)
      .map(
        ([
          nid,
          {
            delta,
            nodeHeightDelta,
            bottomY,
            localBottomY,
            edgeBottomY,
            corridorEnd,
            parentId,
          },
        ]) => ({
          nid,
          delta,
          nodeHeightDelta,
          bottomY,
          localBottomY,
          edgeBottomY,
          corridorEnd,
          parentId,
        }),
      )
      .sort((a, b) => a.bottomY - b.bottomY);

    const currentNodes = get(nodes);
    // Map every node id to its parentId (children inherit their parent's shift)
    const parentMap = {};
    currentNodes.forEach((n) => {
      parentMap[n.id] = n.parentId || null;
    });

    function childShift(origY, parentId, selfId) {
      const rowDeltas = new Map();
      for (const { nid, delta, localBottomY } of expandedEntries) {
        if (
          selfId !== nid &&
          parentMap[nid] === parentId &&
          origY > localBottomY
        ) {
          rowDeltas.set(
            localBottomY,
            Math.max(rowDeltas.get(localBottomY) || 0, delta),
          );
        }
      }
      return Array.from(rowDeltas.values()).reduce(
        (sum, delta) => sum + delta,
        0,
      );
    }

    function parentHeightDelta(parentId) {
      const baseHeight = getNodeHeight({ style: originalNodeStyles[parentId] });
      if (!baseHeight) return 0;

      let originalBottom = 0;
      let shiftedBottom = 0;
      currentNodes.forEach((node) => {
        const original = originalNodePositions[node.id];
        if (node.parentId !== parentId || !original) return;
        const bottom = original.y + calculateNodeHeight(node.data.label);
        originalBottom = Math.max(originalBottom, bottom);
        shiftedBottom = Math.max(
          shiftedBottom,
          bottom + childShift(original.y, parentId, node.id) +
            (expandedNodeDeltas[node.id]?.nodeHeightDelta || 0),
        );
      });
      return Math.max(0, shiftedBottom - originalBottom);
    }

    // Vertical shift applied to a top-level node given its original Y
    function topLevelShift(origY, selfId) {
      let shift = 0;
      const shiftedParents = new Set();

      for (const { nid, delta, bottomY, parentId } of expandedEntries) {
        if (parentId) {
          if (shiftedParents.has(parentId)) continue;
          shiftedParents.add(parentId);
          if (selfId !== parentId && origY > bottomY) {
            shift += parentHeightDelta(parentId);
          }
        } else if (selfId !== nid && origY > bottomY) {
          shift += delta;
        }
      }

      return shift;
    }

    function pointShift(y, edge) {
      let shift = 0;
      const shiftedParents = new Set();

      for (const {
        delta,
        nodeHeightDelta,
        bottomY,
        corridorEnd,
        parentId,
      } of expandedEntries) {
        if (parentId) {
          if (shiftedParents.has(parentId)) continue;
          shiftedParents.add(parentId);
          const parentY = originalNodePositions[parentId]?.y;
          if (
            parentY !== undefined &&
            y <= bottomY &&
            (parentMap[edge.source] === parentId || parentMap[edge.target] === parentId)
          ) {
            // Dagre waypoints are absolute; child positions are relative to the group.
            shift += childShift(y - parentY, parentId, null);
          } else if (y > bottomY) {
            shift += parentHeightDelta(parentId);
          }
        } else if (y > bottomY) {
          // The expanded node grows by nodeHeightDelta, but the rows below only
          // move by delta: the node eats part of the edge corridor between its
          // old bottom and the next row. Waypoints in that corridor (long
          // back-edges run there) therefore get squeezed into the remaining
          // corridor instead of being run over by the node.
          const inCorridor =
            corridorEnd !== null &&
            corridorEnd !== undefined &&
            y < corridorEnd &&
            corridorEnd > bottomY;
          shift += inCorridor
            ? nodeHeightDelta +
              ((y - bottomY) / (corridorEnd - bottomY)) *
                (delta - nodeHeightDelta)
            : delta;
        }
      }

      return shift;
    }

    // Absolute vertical shift of any node; children move with their parent
    function nodeAbsShift(nodeId) {
      const parent = parentMap[nodeId];
      if (parent && originalNodePositions[parent]) {
        const orig = originalNodePositions[nodeId];
        return (
          topLevelShift(originalNodePositions[parent].y, parent) +
          (orig ? childShift(orig.y, parent, nodeId) : 0)
        );
      }
      const orig = originalNodePositions[nodeId];
      return orig ? topLevelShift(orig.y, nodeId) : 0;
    }

    nodes.update((ns) =>
      ns.map((n) => {
        if (!originalNodePositions[n.id]) return n;
        const origY = originalNodePositions[n.id].y;
        const shift = n.parentId
          ? childShift(origY, n.parentId, n.id)
          : topLevelShift(origY, n.id);
        return {
          ...n,
          position: { x: originalNodePositions[n.id].x, y: origY + shift },
          style: n.parentId
            ? n.style
            : resizeNodeHeight(
                originalNodeStyles[n.id] || n.style,
                parentHeightDelta(n.id),
              ),
        };
      }),
    );

    edges.update((es) =>
      es.map((edge) => {
        if (!originalEdgePoints[edge.id]) return edge;
        const origPts = originalEdgePoints[edge.id];

        const firstPt = origPts[0];
        const lastPt = origPts[origPts.length - 1];

        let sourceShift = nodeAbsShift(edge.source);
        const srcExpand = expandedNodeDeltas[edge.source];
        // Only add the expansion delta if the tail exits from the BOTTOM of the source
        // (forward edges). Back edges exit from the TOP — the top border doesn't move
        // when the node expands downward.
        if (
          srcExpand &&
          firstPt &&
          (firstPt.y >= (srcExpand.edgeBottomY ?? srcExpand.bottomY) - 2 ||
            (lastPt && lastPt.y > firstPt.y))
        ) {
          sourceShift += srcExpand.nodeHeightDelta;
        }
        const targetShift = nodeAbsShift(edge.target);

        const nPts = origPts.length;
        const newPts = origPts.map((p, i) => {
          let shift;
          if (i === 0) {
            // Departure point: tied to the bottom of the source node
            shift = sourceShift;
          } else if (i === nPts - 1) {
            // Arrival point: tied to the target node movement
            shift = targetShift;
            // Back edges routed from below arrive at the BOTTOM border of the target
            // (dagre places the last waypoint at bottomY). When the target expands,
            // that border moves down by delta, so we must add it too.
            const tgtExpand = expandedNodeDeltas[edge.target];
            if (
              tgtExpand &&
              p.y >= (tgtExpand.edgeBottomY ?? tgtExpand.bottomY) - 2
            ) {
              shift += tgtExpand.nodeHeightDelta;
            }
          } else {
            // Intermediate waypoints: shift based on their own Y position.
            // Linear interpolation by index is wrong for back-edges (upward curves)
            // because intermediate points can have Y values far outside
            // the [sourceY, targetY] range. Always by position - even when both
            // endpoints move alike, a waypoint in the eaten corridor must not.
            shift = pointShift(p.y, edge);
          }
          return shift !== 0 ? { ...p, y: p.y + shift } : p;
        });
        // Edges that leave (or, coming from below, enter) a NEIGHBOUR in the
        // same row as an expanded node start at that row's old bottom border
        // while everything below moved down: their first diagonal segment
        // cuts through the expanded node's corner. A vertical stub down to the
        // new row bottom keeps them outside (measured: 3 crossings -> 0).
        const rowStub = (nodeId) =>
          expandedEntries.reduce((sum, e) => {
            const own = originalNodePositions[nodeId];
            const exp = originalNodePositions[e.nid];
            return !e.parentId && e.nid !== nodeId && own && exp &&
              Math.abs(own.y - exp.y) < 1
              ? sum + e.nodeHeightDelta
              : sum;
          }, 0);
        const pts = [...newPts];
        const startStub = rowStub(edge.source);
        if (startStub > 0 && firstPt && lastPt && lastPt.y > firstPt.y) {
          pts.splice(1, 0, { ...pts[0], y: pts[0].y + startStub });
        }
        const endStub = rowStub(edge.target);
        if (endStub > 0 && lastPt && firstPt && firstPt.y > lastPt.y &&
            origPts.length > 1 && origPts[origPts.length - 2].y > lastPt.y) {
          const last = pts[pts.length - 1];
          pts.splice(pts.length - 1, 0, { ...last, y: last.y + endStub });
        }
        return { ...edge, data: { ...edge.data, points: pts } };
      }),
    );
  }

  function getNodeHeight(node) {
    const match = node?.style?.match(/height:([0-9.]+)px/);
    return match ? Number(match[1]) : 0;
  }

  function resizeNodeHeight(style, delta) {
    if (!delta || !style) return style;
    const match = style.match(/height:([0-9.]+)px/);
    if (!match) return style;
    return style.replace(match[0], `height:${Number(match[1]) + delta}px`);
  }

  function calculateExpansionLayoutDelta(
    nodeId,
    parentId,
    localBottomY,
    heightDelta,
    nodeCollection,
  ) {
    const nextNodeY = nodeCollection.reduce((closestY, node) => {
      if (
        node.id === nodeId ||
        (node.parentId || null) !== parentId ||
        !originalNodePositions[node.id]
      ) {
        return closestY;
      }
      const candidateY = originalNodePositions[node.id].y;
      return candidateY >= localBottomY && candidateY < closestY
        ? candidateY
        : closestY;
    }, Infinity);

    if (!Number.isFinite(nextNodeY)) {
      return { delta: heightDelta, corridorEnd: null };
    }
    // Everything below moves by the FULL height delta. Letting the node eat the
    // edge corridor (the old EXPANDED_NODE_GAP rule) looked compact, but edges
    // that leave a same-row neighbour at its bottom border and cross the
    // corridor diagonally then ran straight through the expanded node
    // (measured: three crossings on a 60-transition graph).
    return { delta: heightDelta, corridorEnd: nextNodeY };
  }

  // Groessenbeobachter je aufgeklapptem Knoten: der Inhalt (Kennzahlen, Fenster-
  // titel) kommt asynchron, eine feste Wartezeit nach dem Klick misst zu frueh
  // (gemessen: Hoehendelta -6 statt +51) - dann wird nichts verschoben und der
  // Knoten waechst ueber die Kanten.
  let expansionObservers = {};

  function stopObservingNode(nodeId) {
    if (expansionObservers[nodeId]) {
      expansionObservers[nodeId].disconnect();
      delete expansionObservers[nodeId];
    }
  }

  function observeNodeSize(nodeId, nodeEl) {
    stopObservingNode(nodeId);
    if (typeof ResizeObserver === "undefined") return;
    let lastHeight = nodeEl.offsetHeight;
    const observer = new ResizeObserver(() => {
      const height = nodeEl.offsetHeight;
      if (Math.abs(height - lastHeight) < 1) return;
      lastHeight = height;
      measureNodeExpansion(nodeId, nodeEl);
    });
    observer.observe(nodeEl);
    expansionObservers[nodeId] = observer;
  }

  function handleNodeExpand(nodeId, isExpanded) {
    const nodeStore = $nodes.find((n) => n.id === nodeId);
    if (!nodeStore) return;

    if (isExpanded) {
      metricsExpandedNodes.add(nodeId);
      const nodeEl = document.querySelector(`[data-node-id="${nodeId}"]`);
      if (!nodeEl) return;
      measureNodeExpansion(nodeId, nodeEl);
      observeNodeSize(nodeId, nodeEl);
    } else {
      stopObservingNode(nodeId);
      metricsExpandedNodes.delete(nodeId);
      delete expandedNodeDeltas[nodeId];
      applyExpansionDeltas();
    }
  }

  function measureNodeExpansion(nodeId, nodeEl) {
    const nodeStore = $nodes.find((n) => n.id === nodeId);
    if (!nodeStore || !metricsExpandedNodes.has(nodeId)) return;
    {
      const dagreHeight = calculateNodeHeight(nodeStore.data.label);
      const newHeight = nodeEl.offsetHeight;
      const heightDelta = newHeight - dagreHeight;
      if (heightDelta < 1) {
        // (noch) nicht gewachsen: alten Eintrag zuruecknehmen, Beobachter meldet sich
        if (expandedNodeDeltas[nodeId]) {
          delete expandedNodeDeltas[nodeId];
          applyExpansionDeltas();
        }
        return;
      }

      const parentId = nodeStore.parentId || null;
      const localBottomY = originalNodePositions[nodeId]
        ? originalNodePositions[nodeId].y + dagreHeight
        : nodeStore.position.y + dagreHeight;
      // Repeated measurements (e.g. pagination) must use the unexpanded group.
      const parentHeight = getNodeHeight({ style: originalNodeStyles[parentId] });
      const edgeBottomY =
        parentId && originalNodePositions[parentId]
          ? originalNodePositions[parentId].y + localBottomY
          : localBottomY;
      const bottomY =
        parentId && originalNodePositions[parentId] && parentHeight
          ? originalNodePositions[parentId].y + parentHeight
          : localBottomY;
      const { delta: layoutDelta, corridorEnd } = calculateExpansionLayoutDelta(
        nodeId,
        parentId,
        localBottomY,
        heightDelta,
        $nodes,
      );

      expandedNodeDeltas[nodeId] = {
        delta: layoutDelta,
        nodeHeightDelta: heightDelta,
        bottomY,
        localBottomY,
        edgeBottomY,
        corridorEnd,
        parentId,
      };
    }

    applyExpansionDeltas();
  }

  function collapseActivity(label) {
    expandedNodes = expandedNodes.filter((item) => item !== label);
    triggerUpdate = true;
  }

  function onNodeClick(event) {
    const id = event.detail.node.id;
    const node = document.querySelector(`[data-node-id="${id}"]`);
    if (node.classList.contains("node_selected")) {
      node.classList.remove("node_selected");
    } else {
      node.classList.add("node_selected");
    }
    currentActivityId = event.detail.node.parentId.split("_")[0];
    if (!id.match(/^(Start|End)( \d+)?$/gm)) selectedNode = id;
    if (selectedNodes.includes(id)) {
      currentActivityId = "";
      selectedNodes = selectedNodes.filter((item) => item !== id);
      edgesData
        .filter((edge) => edge.from === id || edge.to === id)
        .forEach((edge) => {
          const edgeSelector = `[data-id="${edge.from}-${edge.to}"]`;
          const edgeElement = document.querySelector(edgeSelector);
          if (!edgeElement) return;
          const pathElement = edgeElement.querySelector("path");
          if (pathElement) {
            pathElement.classList.remove("edge_selected");
            let markerId = pathElement.getAttribute("marker-end");
            markerId = markerId.replace("url('#", "").replace("')", "");

            let markedEnd = document.getElementById(markerId);
            if (markedEnd) {
              const polyline = markedEnd.querySelector("polyline");
              if (polyline) {
                polyline.setAttribute("stroke", "black");
                polyline.setAttribute("fill", "black");
              }
            }
          }
        });
    } else {
      selectedNodes = [...selectedNodes, id];
      edgesData
        .filter((edge) => edge.from === id || edge.to === id)
        .forEach((edge) => {
          const edgeSelector = `[data-id="${edge.from}-${edge.to}"]`;
          const edgeElement = document.querySelector(edgeSelector);
          if (!edgeElement) return;
          const pathElement = edgeElement.querySelector("path");
          if (pathElement) {
            pathElement.classList.add("edge_selected");
            let markerId = pathElement.getAttribute("marker-end");
            markerId = markerId.replace("url('#", "").replace("')", "");

            let markedEnd = document.getElementById(markerId);
            if (markedEnd) {
              const polyline = markedEnd.querySelector("polyline");
              if (polyline) {
                polyline.setAttribute("stroke", "#5c25fa");
                polyline.setAttribute("fill", "#5c25fa");
              }
            }
          }
        });
    }
    selectedNodes = selectedNodes;
  }

  var mouseleave = function (id) {
    clearMouseHover();
    edgesData
      .filter((edge) => edge.from === id || edge.to === id)
      .forEach((edge) => {
        const edgeSelector = `[data-id="${CSS.escape(`${edge.from}-${edge.to}`)}"]`;
        const edgeElement = document.querySelector(edgeSelector);
        if (!edgeElement) return;
        const pathElement = edgeElement.querySelector("path");
        if (pathElement) {
          pathElement.classList.remove(
            "edge_hovered_incoming",
            "edge_hovered_outgoing",
          );
          if (selectedNodes.includes(edge.from) || selectedNodes.includes(edge.to)) {
            pathElement.classList.add("edge_selected");
            setEdgeMarker(pathElement, "#5c25fa");
          } else {
            pathElement.classList.remove("edge_selected");
            resetEdgeMarker(pathElement);
          }
        }
      });
  };

  async function applySimulationStep() {
    await tick();
    clearMouseHover();
    document
      .querySelectorAll(".node_hovered")
      .forEach((el) => el.classList.remove("node_hovered"));
    document.querySelectorAll(".edge_hovered").forEach((el) => {
      el.classList.remove("edge_hovered");
      resetEdgeMarker(el);
    });

    if (!simulationPath.length) return;

    if (simulationStep % 2 === 0) {
      const nodeId = simulationPath[Math.floor(simulationStep / 2)];
      const node = document.querySelector(`[data-node-id="${CSS.escape(nodeId)}"]`);
      if (node) node.classList.add("node_hovered");
      return;
    }

    const source = simulationPath[Math.floor(simulationStep / 2)];
    const target = simulationPath[Math.floor(simulationStep / 2) + 1];
    const edgeElement = document.querySelector(`[data-id="${source}-${target}"]`);
    const pathElement = edgeElement?.querySelector("path");
    if (pathElement) {
      pathElement.classList.add("edge_hovered");
      pathElement.setAttribute("stroke-width", "5");
      setEdgeMarker(pathElement, "#ff5f1f");
    }
  }

  function setEdgeMarker(pathElement, color) {
    let markerId = pathElement.getAttribute("marker-end");
    if (!markerId) return;
    markerId = markerId.replace("url('#", "").replace("')", "");
    const markedEnd = document.getElementById(markerId);
    const polyline = markedEnd?.querySelector("polyline");
    if (polyline) {
      polyline.setAttribute("stroke", color);
      polyline.setAttribute("fill", color);
    }
  }

  function resetEdgeMarker(pathElement) {
    pathElement.setAttribute("stroke-width", "2");
    setEdgeMarker(pathElement, "black");
  }

  function clearMouseHover() {
    document
      .querySelectorAll(
        ".edge_hovered_incoming, .edge_hovered_outgoing, .graph_dimmed",
      )
      .forEach((el) =>
        el.classList.remove(
          "edge_hovered_incoming",
          "edge_hovered_outgoing",
          "graph_dimmed",
        ),
      );
    document
      .querySelectorAll(".edge_label_focused")
      .forEach((el) => el.classList.remove("edge_label_focused"));
  }

  function mouseover(evt, id) {
    clearMouseHover();
    document
      .querySelectorAll(".svelte-flow__node, .svelte-flow__edge, .edge-label")
      .forEach((el) => el.classList.add("graph_dimmed"));

    edgesData
      .filter((edge) => edge.from === id || edge.to === id)
      .forEach((edge) => {
        const edgeSelector = `[data-id="${CSS.escape(`${edge.from}-${edge.to}`)}"]`;
        const edgeElement = document.querySelector(edgeSelector);
        if (!edgeElement) return;
        edgeElement.classList.remove("graph_dimmed");
        document
          .querySelector(`[data-edge-id="${CSS.escape(`${edge.from}-${edge.to}`)}"]`)
          ?.classList.remove("graph_dimmed");
        const pathElement = edgeElement.querySelector("path");
        if (pathElement) {
          const isIncoming = edge.to === id;
          pathElement.classList.add(
            isIncoming ? "edge_hovered_incoming" : "edge_hovered_outgoing",
          );
          setEdgeMarker(pathElement, isIncoming ? "#8EC1F0" : "#ff5f1f");
        }
        const nextNode = document.querySelector(`[data-node-id="${CSS.escape(edge.to)}"]`);
        const prevNode = document.querySelector(
          `[data-node-id="${CSS.escape(edge.from)}"]`,
        );
        nextNode?.closest(".svelte-flow__node")?.classList.remove("graph_dimmed");
        prevNode?.closest(".svelte-flow__node")?.classList.remove("graph_dimmed");
      });
  }

  function findEdgeById(edgeId) {
    return edgesData.find((item) => `${item.from}-${item.to}` === edgeId);
  }

  function edgeMouseover(edgeId) {
    clearMouseHover();
    const edge = findEdgeById(edgeId);
    const edgeElement = document.querySelector(`[data-id="${CSS.escape(edgeId)}"]`);
    if (!edge || !edgeElement) return;

    document
      .querySelectorAll(".svelte-flow__node, .svelte-flow__edge, .edge-label")
      .forEach((el) => el.classList.add("graph_dimmed"));

    edgeElement.classList.remove("graph_dimmed");
    const labelElement = document.querySelector(`[data-edge-id="${CSS.escape(edgeId)}"]`);
    labelElement?.classList.remove("graph_dimmed");
    labelElement?.classList.add("edge_label_focused");

    const pathElement = edgeElement.querySelector("path");
    if (pathElement) {
      pathElement.classList.add("edge_hovered_outgoing");
      setEdgeMarker(pathElement, "#ff5f1f");
    }
    [edge.from, edge.to].forEach((nodeId) => {
      document
        .querySelector(`[data-node-id="${CSS.escape(nodeId)}"]`)
        ?.closest(".svelte-flow__node")
        ?.classList.remove("graph_dimmed");
    });
  }

  function edgeMouseleave(edgeId) {
    clearMouseHover();
    document
      .querySelector(`[data-edge-id="${CSS.escape(edgeId)}"]`)
      ?.classList.remove("edge_label_focused");

    const edge = findEdgeById(edgeId);
    const pathElement = document
      .querySelector(`[data-id="${CSS.escape(edgeId)}"]`)
      ?.querySelector("path");
    if (!pathElement) return;
    if (
      edge &&
      (selectedNodes.includes(edge.from) || selectedNodes.includes(edge.to))
    ) {
      pathElement.classList.add("edge_selected");
      setEdgeMarker(pathElement, "#5c25fa");
    } else {
      pathElement.classList.remove("edge_selected");
      resetEdgeMarker(pathElement);
    }
  }

  function closeContextMenu() {
    contextMenuOpen = false;
  }

  function hexToRgba(hex, alpha) {
    hex = hex.replace(/^#/, "");

    if (hex.length === 3) {
      hex = hex
        .split("")
        .map((hexChar) => hexChar + hexChar)
        .join("");
    }

    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);

    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  function parentMouseover(event, id) {
    const targetDiv = document.querySelector(
      `div[data-id="${event.detail.node.id}"]`,
    );
    const contentDiv = targetDiv.querySelector(
      `div[data-node-id="${event.detail.node.id}"]`,
    );
    targetDiv.style.pointerEvents = "none";
    contentDiv.querySelectorAll("*").forEach((element) => {
      element.style.pointerEvents = "auto";
    });
  }

  function getTopApp(nodeId) {
    const listOfApps = kpis[nodeId] ? kpis[nodeId]["apps"] : null;
    if (!listOfApps) return null;
    const sortedApps = Object.entries(listOfApps).sort((a, b) => b[1] - a[1]);
    return sortedApps.length > 0
      ? `${sortedApps[0][0]} ${Math.round(sortedApps[0][1] * 100)}%`
      : null;
  }

  function normalizeOpportunityLabel(value) {
    return String(value || "")
      .replace(/<br\s*\/?>/gi, " ")
      .replace(/<[^>]+>/g, " ")
      .replace(/\s+/g, " ")
      .trim()
      .toLowerCase();
  }

  function getOpportunityClusterLabel(node) {
    if (!node?.parentNode) return "";
    const cluster = (subgraphs || []).find(
      (item) => item.clusterid === node.parentNode,
    );
    return cluster?.label || node.parentNode.replace(/_cluster$/, "");
  }

  function isOpportunityNode(node) {
    const steps = Array.from(opportunitySteps || []).map(normalizeOpportunityLabel);
    if (!steps.length || !node) return false;
    const candidates = [
      node.id,
      node.label,
      node.shortlabel,
      getOpportunityClusterLabel(node),
    ].map(normalizeOpportunityLabel);
    return candidates.some((candidate) =>
      steps.some((step) => candidate === step || candidate.includes(step)),
    );
  }

  function opportunityNodeCandidates(node, includeCluster = false) {
    const candidates = [node.id, node.label, node.shortlabel];
    if (includeCluster) candidates.push(getOpportunityClusterLabel(node));
    return candidates.map(normalizeOpportunityLabel).filter(Boolean);
  }

  function nodeMatchesOpportunity(node, opportunity) {
    const rawSteps = opportunity?.stepImpacts?.length
      ? opportunity.stepImpacts.map((item) => item.step)
      : opportunity?.steps || [];
    const steps = rawSteps.map(normalizeOpportunityLabel);
    if (!steps.length || !node) return false;
    const candidates = opportunityNodeCandidates(node);
    return candidates.some((candidate) =>
      steps.some(
        (step) =>
          candidate === step ||
          (candidate.length >= 8 && step.includes(candidate)) ||
          (step.length >= 8 && candidate.includes(step)),
      ),
    );
  }

  function getNodeReductionForOpportunity(node, opportunity) {
    const stepImpacts = opportunity?.stepImpacts || [];
    const candidates = opportunityNodeCandidates(node);
    const matchingImpacts = stepImpacts.filter((item) => {
      const step = normalizeOpportunityLabel(item.step);
      return candidates.some(
        (candidate) =>
          candidate === step ||
          (candidate.length >= 8 && step.includes(candidate)) ||
          (step.length >= 8 && candidate.includes(step)),
      );
    });

    if (matchingImpacts.length) {
      return Math.max(
        ...matchingImpacts.map((item) => Number(item.reductionPercent || 0)),
      );
    }
    return nodeMatchesOpportunity(node, opportunity)
      ? Number(opportunity.impactPercent || 0)
      : 0;
  }

  function getOpportunityVisual(node) {
    const cards = opportunityCards || [];
    if (!cards.length || !node) return null;

    const matched = cards
      .map((card, index) => ({
        card,
        index,
        reduction: getNodeReductionForOpportunity(node, card),
      }))
      .filter((item) => item.reduction > 0);
    if (!matched.length) return null;

    const reduction = Math.min(
      100,
      matched.reduce((sum, item) => sum + item.reduction, 0),
    );
    return {
      color: matched[0].card.color || "#8EC1F0",
      reduction: Math.round(reduction),
      fullyAutomated: reduction >= 95,
    };
  }

  function getOpportunityReduction(node) {
    const cards = opportunityCards || [];
    if (!cards.length || !node) return 0;
    return Math.min(
      100,
      cards.reduce(
        (sum, card) => sum + getNodeReductionForOpportunity(node, card),
        0,
      ),
    );
  }

  function resetData() {
    return new Promise((resolve) => {
      nodes.set([]);
      edges.set([]);
      //resolve();
      setTimeout(resolve, 0);
    });
  }

  function getMetricValue(nodeId, metric) {
    const nodeKpis = kpis[nodeId];
    if (!nodeKpis || metric === "none") return null;

    if (metric === "text") return nodeKpis["text_share"];
    if (metric === "copy") return nodeKpis["copy_paste_time_share"];
    if (metric === "time") return nodeKpis["time_per_case"];
    if (metric === "rework") {
      return (
        nodeKpis["rework_rate"] ??
        nodeKpis["rework_share"] ??
        nodeKpis["rework"] ??
        null
      );
    }

    return null;
  }

  function normalizeAppName(name) {
    return `${name || ""}`.trim().toLowerCase();
  }

  function normalizeAppType(type) {
    const normalizedType = `${type || ""}`.trim().toLowerCase();
    return normalizedType === "supportive" ? "support" : normalizedType;
  }

  function getAppTypeColor(nodeOrId) {
    const nodeId = typeof nodeOrId === "object" ? nodeOrId?.id : nodeOrId;
    const apps = kpis[nodeId]?.apps || {};
    const topApp = Object.entries(apps)
      .sort((a, b) => Number(b[1]) - Number(a[1]))[0]?.[0];
    const appType = normalizeAppType(appTypesByName[normalizeAppName(topApp)]);

    if (appType === "main") return "#b7e3bd";
    if (appType === "support") return "#e5e8a8";
    if (appType === "communication") return "#abcfe8";
    if (appType === "unwanted") return "#e6a7ad";
    return "#f2f2f2";
  }

  function normalizeClassificationText(value) {
    return String(value || "")
      .toLowerCase()
      .replace(/__run_\d+$/i, "")
      .replace(/<[^>]+>/g, " ");
  }

  function classifyOptimizationNode(node) {
    const label = normalizeClassificationText(
      [node?.id, node?.label, node?.shortlabel].filter(Boolean).join(" "),
    );
    const nodeKpis = kpis[node?.id] || {};
    const copyTime = Number(nodeKpis["copy_paste_time_share"]) || 0;
    const copyRate = Number(nodeKpis["copy_paste_rate"]) || 0;
    const textShare = Number(nodeKpis["text_share"]) || 0;

    if (
      /\b(search|suchen|lookup|find|wait|waiting|warten|rework|nacharbeit|duplicate|double|manual transfer|copy|paste|workaround|download|upload|attach|attachment|anlage|anhang)\b/.test(
        label,
      ) ||
      copyTime >= 0.35 ||
      copyRate >= 0.65
    ) {
      return "waste";
    }

    if (
      /\b(review|prüf|pruef|check|validate|validier|approve|approval|freigabe|control|kontroll|document|dokument|archive|archiv|log|record|compliance|risk|risk management|classification|classify|route|routing)\b/.test(
        label,
      )
    ) {
      return "business-required";
    }

    if (
      /\b(create|compose|draft|respond|reply|address|complete|prepare|solve|solution|calculate|offer|angebot|order|auftrag|customer|kunden|execute|process|update|improve|deliver)\b/.test(
        label,
      ) ||
      textShare >= 0.2
    ) {
      return "value-adding";
    }

    return "business-required";
  }

  function getOptimizationClassificationColor(classification) {
    if (classification === "waste") return "#ff9b85";
    if (classification === "business-required") return "#ffe08a";
    if (classification === "value-adding") return "#9debb0";
    return "#f4f4f4";
  }

  function getMetricColor(nodeOrId, defaultColor) {
    const nodeId = typeof nodeOrId === "object" ? nodeOrId?.id : nodeOrId;
    if (colorMetric === "appType") return getAppTypeColor(nodeOrId);

    if (colorMetric === "optimization:classification") {
      const classification = classifyOptimizationNode(
        typeof nodeOrId === "object" ? nodeOrId : { id: nodeId },
      );
      return getOptimizationClassificationColor(classification);
    }

    if (colorMetric === "optimization:savings") {
      const node = typeof nodeOrId === "object" ? nodeOrId : { id: nodeId };
      const reduction = getOpportunityReduction(node);
      if (reduction <= 0) return "#ffffff";
      const maxReduction = Math.max(
        ...nodesData.map((node) => getOpportunityReduction(node)),
        0,
      );
      const share = maxReduction ? reduction / maxReduction : 0;
      if (share >= 0.66) return "#9debb0";
      if (share >= 0.33) return "#ffe08a";
      return "#ff9b85";
    }

    const value = getMetricValue(nodeId, colorMetric);
    if (value === null || value === undefined) return defaultColor;

    const maxValue = Math.max(
      ...Object.keys(kpis)
        .map((id) => Number(getMetricValue(id, colorMetric)))
        .filter((value) => Number.isFinite(value) && value > 0),
      0,
    );

    if (!maxValue) return defaultColor;

    const share = Number(value) / maxValue;
    if (share >= 0.66) return "#ff9b85";
    if (share >= 0.33) return "#ffe08a";
    return "#9debb0";
  }

  async function setData() {
    if (nodesData.length === 0 || edgesData.length === 0) {
      nodes.set([]);
      edges.set([]);
      filteredEdges = [];
      isLoadingGraph = false;
      return;
    }
    const { positionedNodes, edges: positionedEdges } = calculateNodePositions(
      nodesData,
      edgesData,
      fullNodeInfo,
      minFrequency,
    );

    isLoadingGraph = true;
    const nodeIds = new Set(positionedNodes.map((node) => node.id));

    await tick();

    nodes.set(
      positionedNodes.map((node) => {
        const isParentNode = node.id.endsWith("_cluster");
        const parentNodeExists =
          node.parentNode && nodeIds.has(node.parentNode);
        let posX = node.position.x;
        let posY = node.position.y;
        let finalPos = { x: posX, y: posY };
        const optimizationVisual = getOpportunityVisual(node);
        const optimizationClassification =
          colorMetric === "optimization:classification"
          ? classifyOptimizationNode(node)
          : "";
        const optimizationSaving =
          colorMetric === "optimization:savings"
            ? Math.round(getOpportunityReduction(node))
            : 0;
        const windowSequence =
          windowSequences[node.id] || node.windowSequence || [];
        return {
          id: node.id,
          data: {
            label: node.label,
            actions: node.actions || [],
            icon: node.image,
            isStart: node.label.match(/^Start( \d+)?$/gm) ? true : false,
            isEnd: node.label.match(/^End( \d+)?$/gm) ? true : false,
            color: getMetricColor(node, node.color ? node.color : "#f4f4f4"),
            handlemodal: openNodeMenu,
            isSelected: selectedNodes.includes(node.id),
            position: finalPos,
            handleCollapse: previewMode && isParentNode
              ? () => collapseActivity(node.id.replace(/_cluster$/, ""))
              : collapseActivity,
            expandActivity: expandActivity,
            editActivity,
            explainActivity,
            topApp: getTopApp(node.id),
            optimizationHighlighted: !!optimizationVisual,
            optimizationVisual,
            optimizationClassification,
            optimizationSaving,
            copyShare: kpis[node.id]
              ? Math.round(kpis[node.id]["copy_paste_rate"] * 100)
              : null,
            copyTime: kpis[node.id]
              ? Math.round(kpis[node.id]["copy_paste_time_share"] * 100)
              : null,
            textTime: kpis[node.id]
              ? Math.round(kpis[node.id]["text_share"] * 100)
              : null,
            textCount: kpis[node.id] ? kpis[node.id]["text_count"] : null,
            shortcutCount: kpis[node.id]
              ? kpis[node.id]["shortcut_count"]
              : null,
            windowSequence,
            hasWindowSequence: Boolean(windowSequence.length),
            mouseclicks: kpis[node.id] ? kpis[node.id]["clicks"] : null,
            occuranceRate: kpis[node.id]
              ? Math.round(kpis[node.id]["occurrence_rate"] * 100)
              : null,
            timePerCase: kpis[node.id]
              ? formatMinSec(kpis[node.id]["time_per_case"])
              : null,
            totalTime: kpis[node.id]
              ? formatMinSec(
                  kpis[node.id]["time_per_case"] /
                    kpis[node.id]["occurrence_rate"],
                )
              : null,
            onExpand: handleNodeExpand,
            isChild: !!parentNodeExists,
            isExpanded: metricsExpandedNodes.has(node.id),
            previewMode,
          },
          position: finalPos,
          type: isParentNode ? "group" : "custom",
          dragging: false,
          selectedNode: selectedNode,
          parentId: parentNodeExists ? node.parentNode : "",
          style: isParentNode
            ? `background-color: ${hexToRgba(node.color, 0.3)}; width:${node.width}px; height:${node.height}px; border-radius: 8px; border: 2px solid var(--pax-black);`
            : "",
        };
      }),
    );

    edges.set(
      positionedEdges.map((edge) => ({
        id: edge.id,
        source: edge.source,
        target: edge.target,
        type: "customEdg",
        markerEnd: {
          data_id: edge.id + "_marker",
          type: MarkerType.ArrowClosed,
          width: 20,
          height: 30,
          color: "black",
        },
        data: {
          id: edge.id,
          points: edge.points,
          label: edge.label,
          dashed: edge.dashed,
          alignToNodeBounds: previewMode,
        },
      })),
    );

    $nodes = $nodes;
    isLoadingGraph = false;
    clearMouseHover();

    // Store original positions/points for expansion layout shifting
    originalNodePositions = {};
    originalNodeStyles = {};
    originalEdgePoints = {};
    expandedNodeDeltas = {};
    Object.keys(expansionObservers).forEach(stopObservingNode);
    get(nodes).forEach((n) => {
      originalNodePositions[n.id] = { ...n.position };
      originalNodeStyles[n.id] = n.style || "";
    });
    get(edges).forEach((e) => {
      if (e.data && e.data.points) {
        originalEdgePoints[e.id] = e.data.points.map((p) => ({ ...p }));
      }
    });

    // Re-apply expansion deltas for nodes that were expanded before setData
    if (metricsExpandedNodes.size > 0) {
      await tick();
      for (const nodeId of metricsExpandedNodes) {
        const nodeStore = get(nodes).find((n) => n.id === nodeId);
        if (!nodeStore) {
          metricsExpandedNodes.delete(nodeId);
          continue;
        }
        const nodeEl = document.querySelector(`[data-node-id="${nodeId}"]`);
        if (!nodeEl) continue;
        const dagreHeight = calculateNodeHeight(nodeStore.data.label);
        const newHeight = nodeEl.offsetHeight;
        const heightDelta = newHeight - dagreHeight;
        if (Math.abs(heightDelta) < 1) continue;
        const parentId = nodeStore.parentId || null;
        const localBottomY = originalNodePositions[nodeId]
          ? originalNodePositions[nodeId].y + dagreHeight
          : nodeStore.position.y + dagreHeight;
        const parentHeight = getNodeHeight({ style: originalNodeStyles[parentId] });
        const edgeBottomY =
          parentId && originalNodePositions[parentId]
            ? originalNodePositions[parentId].y + localBottomY
            : localBottomY;
        const bottomY =
          parentId && originalNodePositions[parentId] && parentHeight
            ? originalNodePositions[parentId].y + parentHeight
            : localBottomY;
        const { delta: layoutDelta, corridorEnd } =
          calculateExpansionLayoutDelta(
            nodeId,
            parentId,
            localBottomY,
            heightDelta,
            get(nodes),
          );
        expandedNodeDeltas[nodeId] = {
          delta: layoutDelta,
          nodeHeightDelta: heightDelta,
          bottomY,
          localBottomY,
          edgeBottomY,
          corridorEnd,
          parentId,
        };
      }
      if (Object.keys(expandedNodeDeltas).length > 0) {
        applyExpansionDeltas();
      }
    }
  }

  function extractClassStyles(classNames) {
    let cssText = "";
    const styleSheets = Array.from(document.styleSheets);

    styleSheets.forEach((sheet) => {
      try {
        const rules = Array.from(sheet.cssRules || sheet.rules);
        rules.forEach((rule) => {
          if (rule.type === CSSRule.STYLE_RULE && rule.selectorText) {
            // Check exact match with target classes
            const matchesTarget = classNames.some((className) => {
              // Check if the selector is the class or contains it as a whole class
              return (
                rule.selectorText === className ||
                rule.selectorText.startsWith(`${className}.svelte`) ||
                rule.selectorText.startsWith(`${className} `)
              );
            });

            if (matchesTarget) {
              cssText += rule.cssText + "\n";
            }
          }
        });
      } catch (e) {
        // Ignore CORS errors
      }
    });

    return cssText;
  }

  onMount(async () => {
    if (!isLoadingGraph) {
      await resetData().then(() => setData());
    }
  });

  $$restProps;
</script>

<!-- Kontextmenue Knoten -->
<WorkflowExplorerDropdownMenu
  bind:visible={contextMenuOpen}
  parentNode={currentParentNode}
  position={menuPosition}
  addActivityClicked={addNodesToActivity}
  removeActivityClicked={removeNodeFromActivity}
  expandActivityClicked={expandActivity}
  {isActivityNode}
/>

<div
  bind:this={flowContainerElement}
  class="svelte-flow-container"
  style="width:{width}; height:{height};  display:grid;"
  on:mousedown|capture={closeContextMenu}
  on:wheel|capture={closeContextMenu}
>
  <div style="grid-row: 1; grid-column:1; height:100%;">
    <SvelteFlow
      {nodes}
      {edges}
      {nodeTypes}
      {edgeTypes}
      fitView
      minZoom={0.05}
      nodesDraggable={false}
      connectionMode={ConnectionMode.Loose}
      on:nodeclick={onNodeClick}
      on:openContextMenu={(event) => {
        openNodeMenu(event, event.detail.node.id);
      }}
      on:paneclick={closeContextMenu}
      on:nodemouseenter={(event) => {
        if (!event.detail.node.id.endsWith("_cluster"))
          mouseover(event, event.detail.node.id);
        else {
          parentMouseover(event, event.detail.node.id);
        }
      }}
      on:nodemouseleave={(event) => mouseleave(event.detail.node.id)}
      on:edgemouseenter={(event) => edgeMouseover(event.detail.edge.id)}
      on:edgemouseleave={(event) => edgeMouseleave(event.detail.edge.id)}
      on:window:click={closeContextMenu}
    >
      <Background gap={24} size={1} color="#e0e0e0" />
      {#if seeMinimap}
        <MiniMap zoomable pannable height={120} />
      {/if}
    </SvelteFlow>
  </div>
</div>

<GraphGrouper
  bind:selectedNode
  bind:selectedNodes
  bind:subprocessMenuOpen
  bind:activitiesToShow
  bind:graphActivities
  slot="nodeMenu"
/>

<style>
  :global(.svelteflow-controls-button-fitView) {
    cursor: pointer;
    display: flex;
    height: 3rem;
    width: 3rem;
    align-items: center;
    justify-content: center;
    border-radius: 0.5rem;
    background-color: var(--pax-black);
    color: var(--pax-white);
    transition: 300ms ease-in-out;
  }

  :global(.clusterlabel) {
    width: 100%;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  }

  :global(.more_filled_icon) {
    cursor: pointer;
  }

  :global(.collapseicon) {
    cursor: pointer;
    background-color: white;
    border-radius: 50%;
    border: 1px solid black;
  }
  :global(.collapseicon:hover) {
    background-color: #ff5f1f;
  }
  :global(g.node) {
    cursor: pointer;
  }

  :global(div.nodelabel) {
    display: flex !important;
    gap: 0.5rem;
    align-items: center;
    padding: 0.25rem 0.5rem;
  }

  :global(.svelte-flow__node .node_hovered) {
    border: 3px solid #ff5f1f !important;
    box-shadow: 0 0 0 4px rgba(255, 95, 31, 0.28) !important;
  }

  :global(.svelte-flow__node .node_hovered) {
    stroke: #ff5f1f !important;
  }

  :global(.svelte-flow__edge .edge_hovered) {
    stroke: #ff5f1f !important;
    stroke-width: 5px !important;
  }

  :global(.svelte-flow__edge .edge_hovered defs path) {
    stroke: #ff5f1f !important;
    stroke-width: 5px !important;
  }

  :global(.svelte-flow__edge .edge_hovered_outgoing) {
    stroke: #ff5f1f !important;
    stroke-width: 5px !important;
  }

  :global(.svelte-flow__edge .edge_hovered_outgoing defs path) {
    stroke: #ff5f1f !important;
    stroke-width: 5px !important;
  }

  :global(.svelte-flow__edge .edge_hovered_incoming) {
    stroke: #8EC1F0 !important;
    stroke-width: 5px !important;
  }

  :global(.svelte-flow__edge .edge_hovered_incoming defs path) {
    stroke: #8EC1F0 !important;
    stroke-width: 5px !important;
  }

  :global(.graph_dimmed) {
    opacity: 0.22 !important;
  }

  /* the hovered edge keeps its label above the remaining graph */
  :global(.edge_label_focused) {
    z-index: 200 !important;
  }

  :global(.svelte-flow__edge .edge_selected) {
    stroke: #5c25fa !important;
  }

  :global(.svelte-flow__edge .edge_selected defs path) {
    stroke: #5c25fa !important;
  }

  :global(.edge_selected) {
    stroke: #5c25fa !important;
  }

  :global(.node_selected) {
    stroke: #5c25fa !important;
    border: 2px solid #5c25fa !important;
  }

  :global(foreignObject) {
    overflow: visible !important;
  }

  :global(.svelte-flow__attribution) {
    background-color: transparent !important;
  }
  :global(.svelte-flow__attribution a) {
    background-color: transparent !important;
    color: transparent !important;
  }

  :global(.svelte-flow__node:not(.dragging)) {
    transition: transform 200ms ease;
  }

  :global(.svelte-flow__edge path) {
    transition: d 200ms ease;
  }
</style>
